import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { HomeLanding } from '@/modules/Home/HomeLanding';
import { PanelsOnlyLanding } from '@/modules/Home/PanelsOnlyLanding';
import { modulesForRoles } from '@/constants/modules';

/**
 * Role-based landing for `/`.
 *
 * Per Bill 2026-04-24 (HOME-LANDING brief): the Home tab is the FIRST thing
 * every user sees when they open the Teams app. It's a navigation hub +
 * at-a-glance state widget — greeting, quick actions, KPI strip, three-up
 * widget grid (My Carts · My Tasks · Unclaimed online carts).
 *
 * EVERY role now lands on the unified HomeLanding (Bill 2026-07-13 Item 1 —
 * "make Home the landing page"). The old Admin/SuperAdmin → /admin redirect
 * was the "Operations ghost landing" Bill hit on both smoke tests: the pilot
 * ribbon no longer even shows an Operations tab (batch 2), yet SuperAdmin
 * accounts were still being force-redirected to /admin here before Home ever
 * rendered. Admins reach the operational shell by navigating to /admin
 * directly; it is no longer the landing for anyone.
 */
export function RoleLanding(): JSX.Element {
  const { loading, user } = useAuth();
  if (loading) {
    return (
      <div style={{ padding: 'var(--sp-10)' }}>
        <LoadingSpinner label="Resolving role…" />
      </div>
    );
  }

  // Someone whose roles open no module here (Risk Management / SysOps only) gets an
  // empty rail - send them to the panels instead of a Home offering New Account.
  // Same role union as the shell's rail (Bill 2026-10-07).
  const allRoles = user?.roles && user.roles.length > 0 ? user.roles : user?.primaryRole ? [user.primaryRole] : [];
  if (user && modulesForRoles(allRoles).length === 0) return <PanelsOnlyLanding />;

  // All other roles land on Home. Role gating inside HomeLanding decides which
  // additive widgets render below the primary grid.
  return <HomeLanding />;
}
